import type {
  CashTransferModalStateExternal,
  CashTransferModalStateInternal,
} from '@/store/global/cashTransferModalSlice';
import type {
  CashTransferExternalRequestBody,
  CashTransferInternalRequestBody,
} from '../../tabs/cashTransfers/cashTransfer.model';
import type { IBAN } from './base';
import type { ExternalCashTransferDestination, ExternalCashTransferDetails } from './external';
import type { InternalCashTransferBeneficiary, InternalCashTransferDetails } from './internal';

export type BaseCashTransferForm = {
  communication: string; // 140 chars max
  sourceIBAN: Partial<IBAN>;
};

export type CashTransferFormInternal = BaseCashTransferForm & {
  transferType: 'internal';
  details: InternalCashTransferDetails;
  beneficiary: InternalCashTransferBeneficiary;
};
export type CashTransferFormExternal = BaseCashTransferForm & {
  transferType: 'external';
  details: ExternalCashTransferDetails;
  destination: ExternalCashTransferDestination;
};

export type CashTransferModalState =
  | CashTransferModalStateExternal
  | CashTransferModalStateInternal;

type GetRequestBodyType<T extends CashTransferModalState> = T extends CashTransferModalStateInternal
  ? CashTransferInternalRequestBody
  : CashTransferExternalRequestBody;
export function mapCashTransferFormToRequestPayload<T extends CashTransferModalState>(
  cashTransferFormData: T,
): GetRequestBodyType<T> | undefined {
  const commonData = cashTransferFormData.cashTransferCommonDatas;

  if (cashTransferFormData.transferType === 'internal') {
    const { beneficiaryBank, details, beneficiaryData } = cashTransferFormData;
    const cashTransferRequestPayload: CashTransferInternalRequestBody = {
      communication: commonData.communication,
      debitData: {
        cashTransferTypeCode: 'internal',
        ibanCode: commonData.sourceIBAN?.iban ?? '',
        ibanCurrency: commonData.sourceIBAN?.currency ?? '',
        generalAccountNumber: commonData.sourceIBAN?.generalAccountNumber ?? '',
      },
      detailsData: {
        amount: details?.amount,
        currency: details?.currency,
        executionDate: details?.executionDate,
      },
      beneficiaryBankData: {
        ibanCode: beneficiaryBank?.ibanCode,
        name: beneficiaryBank?.name,
        root: beneficiaryBank?.root,
      },
      beneficiaryData: {
        name: beneficiaryBank?.name ?? beneficiaryData?.name,
      },
    };

    return cashTransferRequestPayload as GetRequestBodyType<T>;
  }

  const { beneficiaryBank, details, beneficiaryData } = cashTransferFormData;

  const cashTransferRequestPayload: CashTransferExternalRequestBody = {
    communication: commonData.communication,
    debitData: {
      cashTransferTypeCode: 'external',
      ibanCode: commonData.sourceIBAN?.iban ?? '',
      ibanCurrency: commonData.sourceIBAN?.currency ?? '',
      generalAccountNumber: commonData.sourceIBAN?.generalAccountNumber ?? '',
    },
    detailsData: {
      amount: details?.amount,
      currency: details?.currency,
      executionDate: details?.executionDate,
      feeTypeCode: details?.feesType,
      feeAmount: details?.feeAmount,
      paymentMethod: details?.paymentMode,
    },
    beneficiaryData: {
      name: beneficiaryData?.name,
      addressData: {
        additionalAddress: beneficiaryData?.addressData?.additionalAddress,
        address: beneficiaryData?.addressData?.address,
        countryCode: beneficiaryData?.addressData?.countryCode,
        number: beneficiaryData?.addressData?.number,
        postalCode: beneficiaryData?.addressData?.postalCode,
        town: beneficiaryData?.addressData?.town,
      },
    },
    beneficiaryBankData: {
      ibanCode: beneficiaryBank?.ibanCode,
      targetAccountType: beneficiaryBank.targetAccountType,
      addressData: {
        countryCode: beneficiaryBank?.addressData?.countryCode,
      },
      swiftAddress: beneficiaryBank?.swiftAddress,
    },
  };

  return cashTransferRequestPayload as GetRequestBodyType<T>;
}
